// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Relatorios/FiltrosRelatorio.tsx
//  Filtros compartilhados pelas telas de relatório: período, pessoa, produto,
//  grupo e CFOP — sempre por nome/busca, nunca por ID digitado.
// ════════════════════════════════════════════════════════════════════════════

import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { PessoaSearchCombobox } from '@/components/PessoaSearchCombobox';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { CfopSearchCombobox } from '@/components/CfopSearchCombobox';
import { useProductGroups } from '@/features/produtos/hooks/useProductGroups';

// ── Datas (no fuso local — toISOString() vira o dia seguinte depois das 21h) ──

const isoLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const hoje      = () => isoLocal(new Date());
export const diasAtras = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return isoLocal(d); };
export const diasFrente = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return isoLocal(d); };
export const anoInicio = () => `${new Date().getFullYear()}-01-01`;

// ── Campo rotulado ────────────────────────────────────────────────────────────

function Campo({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={`flex flex-col gap-1 ${className ?? ''}`}>
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

function BotaoLimpar({ visivel, onClick, titulo }: { visivel: boolean; onClick: () => void; titulo: string }) {
  if (!visivel) return null;
  return (
    <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={onClick} title={titulo}>
      <X className="h-4 w-4" />
    </Button>
  );
}

// ── Período ───────────────────────────────────────────────────────────────────

export function FiltroPeriodo({
  dtInicio, dtFim, onDtInicio, onDtFim, label,
}: {
  dtInicio: string; dtFim: string;
  onDtInicio: (v: string) => void; onDtFim: (v: string) => void;
  label?: string;
}) {
  return (
    <>
      <Campo label={label ? `${label} de` : 'De'}>
        <Input type="date" value={dtInicio} onChange={(e) => onDtInicio(e.target.value)} className="w-40" />
      </Campo>
      <Campo label="Até">
        <Input type="date" value={dtFim} onChange={(e) => onDtFim(e.target.value)} className="w-40" />
      </Campo>
    </>
  );
}

// ── Pessoa (cliente / fornecedor) ─────────────────────────────────────────────

export function FiltroPessoa({
  value, onChange, tipo, label,
}: {
  value?: number; onChange: (v: number | undefined) => void;
  tipo?: 'cliente' | 'fornecedor'; label?: string;
}) {
  const rotulo = label ?? (tipo === 'fornecedor' ? 'Fornecedor' : tipo === 'cliente' ? 'Cliente' : 'Cliente/Fornecedor');
  return (
    <Campo label={rotulo} className="w-72">
      <div className="flex items-center gap-1">
        <div className="flex-1 min-w-0">
          <PessoaSearchCombobox value={value} onChange={(id) => onChange(id || undefined)} filter={tipo}
            placeholder={`Todos (${rotulo.toLowerCase()})`} />
        </div>
        <BotaoLimpar visivel={!!value} onClick={() => onChange(undefined)} titulo={`Limpar ${rotulo.toLowerCase()}`} />
      </div>
    </Campo>
  );
}

// ── Produto ───────────────────────────────────────────────────────────────────

export function FiltroProduto({ value, onChange }: { value?: number; onChange: (v: number | undefined) => void }) {
  return (
    <Campo label="Produto" className="w-72">
      <div className="flex items-center gap-1">
        <div className="flex-1 min-w-0">
          <ProdutoSearchCombobox value={value} onChange={(id) => onChange(id || undefined)} placeholder="Todos os produtos" />
        </div>
        <BotaoLimpar visivel={!!value} onClick={() => onChange(undefined)} titulo="Limpar produto" />
      </div>
    </Campo>
  );
}

// ── Grupo de produto ──────────────────────────────────────────────────────────

const TODOS = '__todos__';

export function FiltroGrupoProduto({ value, onChange }: { value?: number; onChange: (v: number | undefined) => void }) {
  const { groups, isLoading } = useProductGroups();
  const ativos = groups
    .filter((g) => !g.isInativo || g.id === value)
    .sort((a, b) => (a.descricao ?? '').localeCompare(b.descricao ?? '', 'pt-BR'));
  return (
    <Campo label="Grupo">
      <Select value={value ? String(value) : TODOS} onValueChange={(v) => onChange(v === TODOS ? undefined : Number(v))}
        disabled={isLoading}>
        <SelectTrigger className="w-52"><SelectValue placeholder="Todos os grupos" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={TODOS}>Todos os grupos</SelectItem>
          {ativos.map((g) => (
            <SelectItem key={g.id} value={String(g.id)}>{g.descricao || '(sem descrição)'}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Campo>
  );
}

// ── CFOP ──────────────────────────────────────────────────────────────────────

export function FiltroCfop({ value, onChange }: { value?: string; onChange: (v: string | undefined) => void }) {
  return (
    <Campo label="CFOP" className="w-64">
      {/* O próprio combobox já tem o botão de limpar. */}
      <CfopSearchCombobox value={value ? Number(value) : null}
        onChange={(c) => onChange(c ? String(c).replace('.', '') : undefined)} placeholder="Todos os CFOPs" />
    </Campo>
  );
}
